import {useCallback, useRef, useEffect} from 'react';

import {fetchCodeForServiceOrProductsFx} from 'models/documents/invoices/create';

export function useHsnFetching(isHsn?: boolean) {
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingResolverRef = useRef<((value: {label: string; value: string}[]) => void) | null>(
        null,
    );
    const lastRequestedValueRef = useRef<string>('');

    useEffect(
        () => () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
            if (pendingResolverRef.current) {
                pendingResolverRef.current([]);
                pendingResolverRef.current = null;
            }
        },
        [],
    );

    return useCallback(
        (val: string) => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }

            if (pendingResolverRef.current) {
                pendingResolverRef.current([]);
                pendingResolverRef.current = null;
            }

            return new Promise<{label: string; value: string}[]>((resolve) => {
                pendingResolverRef.current = resolve;
                lastRequestedValueRef.current = val;

                if (val.length < 3) {
                    resolve([]);
                    pendingResolverRef.current = null;
                    return;
                }

                const currentQuery = val;

                timeoutIdRef.current = setTimeout(async () => {
                    timeoutIdRef.current = null;

                    try {
                        const result = (await fetchCodeForServiceOrProductsFx({
                            isService: isHsn ?? true,
                            params: {
                                code: currentQuery,
                                size: 99,
                                number: 0,
                            },
                        })) as {
                            data: {
                                code: string;
                                nameHierarch: string;
                                name: string;
                            }[];
                        };

                        if (lastRequestedValueRef.current !== currentQuery) {
                            resolve([]);
                            return;
                        }

                        resolve(
                            result.data?.map((i) => ({
                                label: i.name,
                                value: i.code,
                                name: i.nameHierarch,
                            })) ?? [],
                        );
                    } catch (error) {
                        resolve([]);
                    } finally {
                        pendingResolverRef.current = null;
                    }
                }, 300);
            });
        },
        [isHsn],
    );
}
